/**
 * Classic (non-quiz) unit renderer.
 */
import React, { Fragment } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";

import { useAsterController } from "context";
import HtmlRenderer from "pages/common/HtmlRenderer";
import SafeHtml from "pages/common/SafeHtml";
import VimeoComponent from "pages/common/VimeoComponent";
import YoutubeComponent from "pages/common/YoutubeComponent";
import Problem from "pages/board/problems/Problem";
import { getSectionTitle, getVerticalTitle } from "utils/course";
import { getGradeForBlock } from "utils/grade.util";
import { asArray, asFunc, asObject } from "utils/safeData";
import { getSimilarity } from "utils/string";

/**
 * Renders HTML, video, and problem XBlocks for a standard unit.
 *
 * @param {{ xblocks?: Array, onSubmitProblem?: Function }} props
 */
const ClassicModuleRender = (props) => {
  const currCourse = asObject(props.currCourse);
  const xblocks = asArray(props.xblocks);
  const onSubmitProblem = asFunc(props.onSubmitProblem);
  const { sectionIndex, sequenceIndex, verticalIndex } = useParams();

  const [controller] = useAsterController();
  const { structure: courseStructure } = controller;

  const sectionTitle = getSectionTitle(courseStructure, sectionIndex);

  // Compare to section title to show or not.
  const isShowAvailable = (str) => {
    const r = getSimilarity(sectionTitle, str);
    return r > 0.17;
  };

  const renderHtml = (block, key) => {
    // If display name is equal to vertical name, we don't display double titles
    const flag =
      getVerticalTitle(courseStructure, sectionIndex, sequenceIndex, verticalIndex) !== block.display_name &&
      key === 0 &&
      block.display_name !== "Raw HTML";

    return (
      <Fragment key={block.id}>
        {flag && isShowAvailable(block.display_name) && (
          <div className="mt-4 fsp-28 font-bold">{block.display_name}</div>
        )}

        <HtmlRenderer className="mt-4" html={block.html} />
      </Fragment>
    );
  };

  return (
    <>
      {xblocks.map((block, key) => {
        switch (block.type) {
          case "html":
            return renderHtml(block, key);

          case "google-document":
            return (
              <SafeHtml
                key={block.id}
                className="google-docs-xblock-wrapper mt-4"
                html={block.raw_html}
                course
              />
            );

          case "scorm": {
            const scormHtml = String(block.raw_html || "");
            const heightMatch = scormHtml.match(/height=["']?([^"'>\s]+)/i);
            const height = heightMatch ? heightMatch[1] : "600";
            const base = String(process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
            // Prefer unit sha, then any sha already embedded in structure HTML.
            const fromUnit = String(block.html || "").trim();
            const fromEmbed = (scormHtml.match(/\/by-sha\/([a-f0-9]{40})\//i) || [])[1] || "";
            const hash = /^[a-f0-9]{40}$/i.test(fromUnit) ? fromUnit : fromEmbed;
            let src = "";
            if (hash && base) {
              src = `${base}/media/scorm/by-sha/${hash}/index.html`;
            } else {
              const srcMatch = scormHtml.replace(/VAR_SCORM_HASH/g, hash).match(/src=["']([^"']+)["']/i);
              src = srcMatch ? srcMatch[1] : "";
            }
            // Empty /by-sha// loads themed LMS 404 HTML inside the iframe.
            if (!src || /VAR_SCORM_HASH|\/by-sha\/\/|\/scorm\/\/|\/by-sha\/(?:index\.html)?$/i.test(src)) {
              return (
                <div key={block.id} className="iframe-xblock-wrapper mt-4 v-content-iframe">
                  <p className="mt-4 text-secondary">Loading interactive module…</p>
                </div>
              );
            }
            return (
              <div key={block.id} className="iframe-xblock-wrapper mt-4 v-content-iframe">
                <iframe
                  title={block.display_name || "SCORM"}
                  src={src}
                  width="100%"
                  height={height}
                  // No allow-top-navigation: LMS themed 404s must not steal the learner app window.
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  referrerPolicy="no-referrer"
                />
              </div>
            );
          }

          case "lti":
          case "lti_consumer":
            return (
              <SafeHtml
                key={block.id}
                className="iframe-xblock-wrapper mt-4 v-content-iframe"
                html={block.raw_html}
                course
              />
            );

          case "problem":
            return (
              <Problem
                key={block.id}
                rawText={block.html}
                grade={getGradeForBlock(currCourse, block.id)}
                courseId={courseStructure.course_id}
                blockId={block.id}
                onSubmitProblem={onSubmitProblem}
              />
            );

          case "videoxblock":
            return (
              <Fragment key={block.id}>
                {getVerticalTitle(courseStructure, sectionIndex, sequenceIndex, verticalIndex) !== block.display_name &&
                  isShowAvailable(block.display_name) && <div className="mt-4 fsp-28 font-bold">{block.display_name}</div>}

                <VimeoComponent
                  vimeoId={block.vimeo_id}
                  moduleTitle={getSectionTitle(courseStructure, sectionIndex)}
                  vimeoTitle={block.display_name}
                />
              </Fragment>
            );

          case "video":
          case "invideomodule":
            return <YoutubeComponent key={block.id} embedId={block.vimeo_id || block.definition_id} />;

          default:
            // fallback: render as html (some blocks deliver content in block.html)
            return renderHtml(block, key);
        }
      })}
    </>
  );
};

ClassicModuleRender.propTypes = {
  currCourse: PropTypes.object,
  xblocks: PropTypes.array,
  onSubmitProblem: PropTypes.func,
};

export default ClassicModuleRender;
